import React from 'react';

import { useTranslation } from 'react-i18next';

import GUIDE_ACTION_TYPES from 'config/constants/GUIDE_ACTION_TYPES';
import { guideStatusResolver } from 'config/translatableConstants/TRANSLATABLE_GUIDE_STATUSES';
import parseDate from 'helpers/parseDate';
import general_messages from 'messages/general_messages';
import guide_messages from 'messages/guide_messages';
import userDataStorage from 'storages/userDataStorage';

const PRINT_STYLES = `
.ps-guide-print { background: #fff; color: #000; padding: 16px; font-size: 14px; line-height: 1.45; }
.ps-guide-print h1 { font-size: 24px; margin: 0 0 16px; }
.ps-guide-print h2 { font-size: 18px; margin: 24px 0 8px; break-after: avoid; }
.ps-guide-print h3 { font-size: 16px; margin: 0 0 8px; break-after: avoid; }
.ps-guide-print__meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 20px; margin-bottom: 20px; }
.ps-guide-print__label { font-weight: 700; }
.ps-guide-print__step { border: 1px solid #000; padding: 12px; margin: 0 0 16px; break-inside: avoid; page-break-inside: avoid; }
.ps-guide-print__description { margin: 6px 0 12px; white-space: pre-wrap; }
.ps-guide-print__action { border-top: 1px solid #999; padding-top: 8px; margin-top: 8px; }
.ps-guide-print__status { font-weight: 700; }
.ps-guide-print__free-text,
.ps-guide-print__comment { margin-top: 8px; white-space: pre-wrap; }
@media print {
  .ps-guide-print { padding: 0; }
  .ps-guide-print__meta { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
`;

type GuideProgressPrintTemplateProps = {
  guide: any;
  progress: any;
  steps: GuidePrintStep[];
};

type GuidePrintAction = {
  id: string;
  action_type?: string;
  description?: string;
  heading: string;
  user_data?: {
    freetext?: string;
  } | null;
};

type GuidePrintStep = {
  id: string;
  actions?: GuidePrintAction[];
  description?: string;
  heading: string;
  user_data?: {
    comment?: string;
  } | null;
};

const GuideProgressPrintTemplate: React.FC<GuideProgressPrintTemplateProps> = ({ guide, progress, steps }) => {
  const { t } = useTranslation();
  const currentUser = userDataStorage.get() || {};
  const displayName = progress.display_name || guide.heading || t(general_messages.guide);
  const hasCustomTitle = !!progress.title;
  const userName = [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') || currentUser.email || '-';

  const metadata = [
    [guide_messages.report_information_labels.name, displayName],
    ...(hasCustomTitle ? [[guide_messages.report_information_labels.template_name, guide.heading]] : []),
    [guide_messages.report_information_labels.guide_version, guide.version_number],
    [guide_messages.report_information_labels.related_asset, progress.asset_name || t(general_messages.no_data)],
    [guide_messages.report_information_labels.status, t(guideStatusResolver(progress.progress_status))],
    [guide_messages.report_information_labels.start, parseDate(progress.created_at)],
    [guide_messages.report_information_labels.last_edit, parseDate(progress.updated_at)],
    [guide_messages.report_information_labels.completed_at, parseDate(progress.completed_at)],
    [guide_messages.progress_user, userName],
    [guide_messages.progress_organization, currentUser.organization || '-'],
  ];

  return (
    <article className='ps-guide-print'>
      <style>{PRINT_STYLES}</style>
      <h1>{displayName}</h1>
      <section className='ps-guide-print__meta'>
        {metadata.map(([label, value]) => (
          <div key={`${label[0]}-${value}`}>
            <span className='ps-guide-print__label'>{t(label)}: </span>
            <span>{value || '-'}</span>
          </div>
        ))}
      </section>

      <h2>{t(guide_messages.steps)}</h2>
      {steps.map(step => (
        <section key={step.id} className='ps-guide-print__step'>
          <h3>{step.heading}</h3>
          {step.description && <div className='ps-guide-print__description'>{step.description}</div>}
          {step.user_data?.comment && (
            <div className='ps-guide-print__comment'>
              <span className='ps-guide-print__label'>{t(general_messages.comments)}: </span>
              {step.user_data.comment}
            </div>
          )}
          {step.actions?.map(action => {
            const checked = !!action.user_data;
            return (
              <div key={action.id} className='ps-guide-print__action'>
                <div>
                  <span className='ps-guide-print__status'>{checked ? '[x]' : '[ ]'} </span>
                  <span>{action.heading}</span>
                </div>
                {action.description && <div className='ps-guide-print__description'>{action.description}</div>}
                {action.action_type === GUIDE_ACTION_TYPES.FREETEXT && action.user_data?.freetext && (
                  <div className='ps-guide-print__free-text'>
                    <span className='ps-guide-print__label'>{t(guide_messages.action_types.freetext)}: </span>
                    {action.user_data.freetext}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </article>
  );
};

export default GuideProgressPrintTemplate;
